import { Component, OnInit } from '@angular/core';
import {
    CreatePromoCode,
    Entitlement,
    PremiumAdminService,
    PromoCode,
    PromoRedemption,
    UserHit,
    WebhookEvent
} from '@services/premium-admin.service';

type Tab = 'codes' | 'users' | 'events';

@Component({
    selector: 'app-premium-page',
    templateUrl: './premium-page.component.html'
})
export class PremiumPageComponent implements OnInit {
    tab: Tab = 'codes';
    message = '';
    error = '';

    // Promo codes
    codes: PromoCode[] = [];
    newCode: CreatePromoCode = this.emptyCode();
    selectedCode: PromoCode | null = null;
    redemptions: PromoRedemption[] = [];

    // Users
    userQuery = '';
    hits: UserHit[] = [];
    selectedUser: UserHit | null = null;
    entitlements: Entitlement[] = [];
    grantDays: number | null = 30;
    grantLifetime = false;
    grantNote = '';

    // Webhook events
    events: WebhookEvent[] = [];
    eventFilter = '';

    constructor(private api: PremiumAdminService) {}

    ngOnInit() {
        this.loadCodes();
    }

    setTab(t: Tab) {
        this.tab = t;
        this.message = '';
        this.error = '';
        if (t === 'events') this.loadEvents();
        if (t === 'codes') this.loadCodes();
    }

    // ---- Promo codes ----
    loadCodes() {
        this.api.listPromoCodes().subscribe(r => (this.codes = r), e => this.fail(e));
    }

    createCode() {
        const dto: any = { ...this.newCode };
        // Date-only values from the date input mean the END of the chosen day; empty strings become null.
        dto.validFrom = dto.validFrom || null;
        dto.validUntil = dto.validUntil ? (/^\d{4}-\d{2}-\d{2}$/.test(dto.validUntil) ? `${dto.validUntil}T23:59:59` : dto.validUntil) : null;
        if (!dto.code || !dto.code.trim()) delete dto.code;
        this.api.createPromoCode(dto).subscribe(c => {
            this.ok(`Kod oluşturuldu: ${c.code}`);
            this.newCode = this.emptyCode();
            this.loadCodes();
        }, e => this.fail(e));
    }

    toggleCode(c: PromoCode) {
        this.api.setPromoActive(c.id, !c.active).subscribe(() => this.loadCodes(), e => this.fail(e));
    }

    showRedemptions(c: PromoCode) {
        this.selectedCode = c;
        this.api.listRedemptions(c.id).subscribe(r => (this.redemptions = r), e => this.fail(e));
    }

    revoke(entitlementId: number) {
        if (!confirm('Bu premium hakkı iptal edilsin mi? Kullanıcının başka aktif hakkı yoksa premium sona erer.')) return;
        this.api.revokeEntitlement(entitlementId).subscribe(() => {
            this.ok('Hak iptal edildi');
            if (this.selectedCode) this.showRedemptions(this.selectedCode);
            if (this.selectedUser) this.loadEntitlements(this.selectedUser);
        }, e => this.fail(e));
    }

    // ---- Users ----
    search() {
        this.api.searchUsers(this.userQuery).subscribe(r => (this.hits = r), e => this.fail(e));
    }

    pickUser(u: UserHit) {
        this.selectedUser = u;
        this.loadEntitlements(u);
    }

    loadEntitlements(u: UserHit) {
        this.api.userEntitlements(u.id).subscribe(r => (this.entitlements = r), e => this.fail(e));
    }

    grant() {
        if (!this.selectedUser) return;
        const days = this.grantLifetime ? null : this.grantDays;
        this.api.grantManual(this.selectedUser.id, days, this.grantNote).subscribe(() => {
            this.ok('Premium verildi');
            this.grantNote = '';
            this.loadEntitlements(this.selectedUser!);
        }, e => this.fail(e));
    }

    // ---- Webhook events ----
    loadEvents() {
        this.api.listWebhookEvents(this.eventFilter, 1, 50).subscribe(r => (this.events = r), e => this.fail(e));
    }

    reprocess(ev: WebhookEvent) {
        this.api.reprocessEvent(ev.id).subscribe(r => {
            this.ok(`Yeniden işlendi: ${r.outcome}`);
            this.loadEvents();
        }, e => this.fail(e));
    }

    private ok(m: string) { this.message = m; this.error = ''; }
    private fail(e: any) {
        this.error = e?.error?.error ? String(e.error.error) : `Hata (${e?.status ?? '?'})`;
        this.message = '';
    }

    private emptyCode(): CreatePromoCode {
        return { code: '', description: '', durationDays: 30, maxRedemptions: null, validFrom: null, validUntil: null };
    }
}
